import {
  toIdArray,
  type IdValue
} from '../../../../../utils/companyCategorySubCategory';

import {
  get75PeerBenchmarkData,
  get6PeerBenchmarkData,
  getDefaultPeerBenchmarkData,
  type PeerBenchmarkMetricView,
  type PeerBenchmarkPillarRow,
  get2PeerBenchmarkData,
  get9PeerBenchmarkData,
  get83PeerBenchmarkData,
  get83EmptyPeerBenchmarkData,
  get3PeerBenchmarkData
} from './peerBenchmarkingDefaultData';

/**
 * Keys: `subCategory::year` or `subCategory::*`.
 * `year` may be `2023` (calendar / single picker) or `2023-2024` (FY range). Lookup tries the
 * incoming value first, then the alternate form so one override row can match either shape.
 */
const PEER_BENCHMARK_OVERRIDES: Record<string, PeerBenchmarkPillarRow[]> = {
  // Sample: fictional subCategory/year so production routing is unchanged — swap for real IDs + replace array body
  '6::2023-2024': get6PeerBenchmarkData(),
  '6::2026-2027': get6PeerBenchmarkData(),
  '75::2026-2027': get75PeerBenchmarkData(),
  '75::2023-2024': get75PeerBenchmarkData(),
  '2::2026-2027': get2PeerBenchmarkData(),
  '2::2023-2024': get2PeerBenchmarkData(),
  '9::2026-2027': get9PeerBenchmarkData(),
  '9::2023-2024': get9PeerBenchmarkData(),
  '83::2025-2026': get83PeerBenchmarkData(),
  '83::2026-2027': get83PeerBenchmarkData(),
  '83::*': get83EmptyPeerBenchmarkData(),
  '3::2026-2027': get3PeerBenchmarkData(),
  '3::2023-2024': get3PeerBenchmarkData()
};

export function resolvePeerBenchmarkMetricView(
  pillar: PeerBenchmarkPillarRow,
  metricValue: string
): PeerBenchmarkMetricView {
  const override = pillar.metricData?.[metricValue];
  if (override) {
    return {
      average: override.average ?? pillar.average,
      averageDetail: override.averageDetail ?? pillar.averageDetail,
      companyData: override.companyData ?? pillar.companyData
    };
  }

  return {
    average: pillar.average,
    averageDetail: pillar.averageDetail,
    companyData: pillar.companyData
  };
}

function overrideKey(subCategory: number, year: string): string {
  return `${subCategory}::${year}`;
}

/** Same FY in both notations used by `yearSelect` (single vs Apr–Mar range). */
function peerYearLookupKeys(year: string): string[] {
  const t = year.trim();
  if (!t) {
    return [];
  }
  const keys: string[] = [t];
  const rangeMatch = /^(\d{4})-(\d{4})$/.exec(t);
  if (rangeMatch) {
    const start = rangeMatch[1];
    if (start && !keys.includes(start)) {
      keys.push(start);
    }

    return keys;
  }
  if (/^\d{4}$/.test(t)) {
    const y = Number(t);
    const rangeKey = `${y}-${y + 1}`;
    if (!keys.includes(rangeKey)) {
      keys.push(rangeKey);
    }
  }

  return keys;
}

function resolvePeerBenchmarkForId(
  subCategory: number,
  year: string
): PeerBenchmarkPillarRow[] | undefined {
  const normalizedYear = year?.trim() ?? '';

  if (normalizedYear) {
    for (const yKey of peerYearLookupKeys(normalizedYear)) {
      const exact = PEER_BENCHMARK_OVERRIDES[overrideKey(subCategory, yKey)];
      if (exact?.length) {
        return exact;
      }
    }
  }

  const subCategoryWildcard =
    PEER_BENCHMARK_OVERRIDES[overrideKey(subCategory, '*')];
  if (subCategoryWildcard?.length) {
    return subCategoryWildcard;
  }

  return undefined;
}

export function getPeerBenchmarkDataArray(
  subCategory: IdValue,
  year: string
): PeerBenchmarkPillarRow[] {
  for (const id of toIdArray(subCategory)) {
    const resolved = resolvePeerBenchmarkForId(id, year);
    if (resolved) {
      return resolved;
    }
  }

  return getDefaultPeerBenchmarkData();
}
